import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { EmptyIndicator, LoadingIndicator } from 'components/common';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';
import {
  ProfileAmbitions,
  ProfileCompetencies,
  ProfileSummaryGroups,
  ProfileSummaryInfo,
  ProfileSummaryTimeline,
  ProfileSummaryUpcoming,
  ProfileSupervisors
} from 'components/profile';

interface Props {
  isVisible: boolean;
  onHide: () => void;
  targetUserId: number;
}

const UsersPreview = ({ isVisible, onHide, targetUserId }: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const { data, loading } = useApiData({
    url: `/admin/users?id=${targetUserId}`,
    onlyIf: Boolean(targetUserId)
  });

  const targetUser = data?.users?.[0];

  return (
    <ModalPortal>
      <Modal
        closeButton={true}
        onHide={onHide}
        title={messages.commonUser}
        visible={isVisible}
        width="wide"
      >
        <ModalContent style={{ backgroundColor: theme.lightGreyLighter }}>
          {loading ? (
            <LoadingIndicator />
          ) : !targetUser ? (
            <div className="empty">
              <EmptyIndicator
                icon="user"
                size="small"
                title={messages.settingsAddAdminUserNotFound}
              />
            </div>
          ) : (
            <div className="preview">
              <div className="main">
                <ProfileSummaryInfo
                  isPreview={true}
                  isUserPage={true}
                  targetUser={targetUser}
                />

                {!targetUser?.roles?.includes('guest') ? (
                  <>
                    <ProfileCompetencies
                      apiUrl="/admin/users"
                      isPreview={true}
                      isUserPage={true}
                      targetUser={targetUser}
                    />
                    {!targetUser.isLimitedUser && (
                      <>
                        <ProfileAmbitions
                          isPreview={true}
                          isUserPage={true}
                          targetUser={targetUser}
                        />

                        <ProfileSupervisors
                          isUserPage={true}
                          targetUserId={targetUser.id}
                        />

                        <ProfileSummaryGroups targetUserId={targetUser.id} />
                      </>
                    )}
                  </>
                ) : null}
              </div>
              {!targetUser.isLimitedUser &&
                !serverConfig.platformCompetenciesOnly && (
                  <div className="side">
                    <ProfileSummaryTimeline
                      isUserPage={true}
                      targetUserId={targetUser.id}
                    />

                    <ProfileSummaryUpcoming
                      isUserPage={true}
                      targetUserId={targetUser.id}
                    />
                  </div>
                )}

              <style jsx>{`
                .preview {
                  align-items: flex-start;
                  display: flex;
                  flex-direction: column;
                  gap: 20px;
                }
                .main {
                  display: flex;
                  flex-direction: column;
                  flex-grow: 1;
                  gap: 20px;
                }
                .side {
                  display: flex;
                  flex-direction: column;
                  flex-grow: 0;
                  flex-shrink: 0;
                  gap: 20px;
                  width: 100%;
                }
                @media (min-width: ${breakpoints.large}px) {
                  .preview {
                    flex-direction: row;
                  }
                  .side {
                    width: 400px;
                  }
                }
              `}</style>
            </div>
          )}
        </ModalContent>
      </Modal>
    </ModalPortal>
  );
};

export default UsersPreview;
